import { Button } from "flowbite-react";
import { useEffect, useState } from "react";

import { useImpersonation } from "../../hooks/useImpersonation";
import { ImpersonationPickerModal } from "./ImpersonationPickerModal";

const formatRemaining = (expiresAt) => {
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return "expired";
  const minutes = Math.floor(ms / 60000);
  if (minutes < 1) return "<1m";
  return `${minutes}m`;
};

/**
 * Top-of-screen banner for superuser impersonation.
 *
 * - Hidden entirely unless the user is an eligible superuser OR an
 *   impersonation session is active.
 * - When active: red banner spans the top with target email, time left,
 *   and a Release button.
 * - When not active but eligible: a compact yellow "Impersonate user" pill
 *   that opens the picker modal.
 *
 * The banner doesn't gate any other UI — every API call already runs as
 * whichever identity the JWT cookie represents.
 */
export const ImpersonationBanner = () => {
  const { status, loading, start, release, searchCandidates } =
    useImpersonation();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!status?.impersonating) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 30 * 1000);
    return () => clearInterval(id);
  }, [status?.impersonating]);

  if (loading || !status) return null;
  if (!status.feature_enabled && !status.impersonating) return null;
  if (!status.is_superuser_eligible && !status.impersonating) return null;

  if (status.impersonating) {
    const session = status.session;
    const handleRelease = async () => {
      setReleasing(true);
      try {
        await release();
      } catch (_err) {
        setReleasing(false);
      }
    };
    return (
      <div
        role="alert"
        className="sticky top-0 z-[9999] flex flex-wrap items-center justify-between gap-2 bg-red-600 px-4 py-2 text-white shadow"
      >
        <div className="text-sm">
          <span className="font-semibold">Viewing as</span>{" "}
          <span className="font-mono">{session.target_user_email}</span>
          <span className="ml-2 opacity-80">
            (session ends in {formatRemaining(session.expires_at)}; changes you
            make are real)
            {tick >= 0 ? "" : ""}
          </span>
        </div>
        <Button
          color="light"
          size="xs"
          onClick={handleRelease}
          disabled={releasing}
        >
          {releasing ? "Releasing…" : "Release"}
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className="fixed bottom-4 right-4 z-[9998]">
        <Button color="warning" size="xs" onClick={() => setPickerOpen(true)}>
          Impersonate user
        </Button>
      </div>
      <ImpersonationPickerModal
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        searchCandidates={searchCandidates}
        onStart={start}
      />
    </>
  );
};
